import { useLocation } from 'react-router-dom';
import Lottie from 'lottie-react';
import loaderAnimation from '../../assets/lottie/Loader.json';
import { LOADER_LABELS } from './translations/labels';

function getLoaderText(label: string): string {
  switch (label) {
    case 'edit-facility':
    case 'edit-client-step-2':
      return 'Ładowanie danych placówki…';
    case 'edit-client':
      return 'Ładowanie danych klienta…';
    case 'szczegółów formularza zadania…':
      return 'Ładowanie szczegółów formularza zadania…';
    case 'szczegółów planu…':
      return 'Ładowanie szczegółów planu…';
    case 'Edycja planu':
      return 'Ładowanie danych Edycja planu';
    case 'Utwórz plan':
      return 'Ładowanie…';
    case 'Utwórz ofertę':
      return 'Ładowanie…';
    case 'szczegółów zgłoszenia kontaktowego…':
      return 'Loading contact request details…';
    case 'certificate-template-view':
      return LOADER_LABELS.CERTIFICATE_TEMPLATE_LOADING.polish;
    case 'certificate-template-edit':
      return LOADER_LABELS.CERTIFICATE_TEMPLATE_LOADING.polish;
    case 'certificate-view':
      return 'Loading certificate details...';
    case 'submitted-form-view':
      return 'Ładowanie szczegółów przesłanego formularza…';
    case 'add-admin':
    case 'add-service-type':
      return 'Ładowanie…';
    default:
      return `Ładowanie danych ${label}`;
  }
}

function getLoaderLabel(pathname: string): string {
  const pathWithoutQuery = pathname.replace(/^\//, '').split('?')[0] ?? '';
  const pathDecoded = decodeURI(pathWithoutQuery);
  const segments = pathDecoded.split('/').filter(Boolean);
  if (segments[0] === 'Moje-certyfikaty' && segments.length > 1) {
    return 'certificate-view';
  }
  if (segments[0] === 'Placówki' && segments.includes('Edycja-placówki')) {
    return 'edit-facility';
  }
  if (segments[0] === 'Placówki' && segments.length > 1) {
    return 'Podgląd placówki';
  }
  if (
    segments[0] === 'Produkty i usługi' &&
    segments[1] &&
    segments[1] !== 'Podgląd-zgłoszenia'
  ) {
    return 'Zamów usługę';
  }

  const routeKey = `${segments[0]}/${segments[1]}`;
  switch (routeKey) {
    case 'administrators/Podgląd-Administratora':
      return 'Podgląd Administratora';
    case 'administrators/Edycja-Administratora':
      return 'Edycja-Administratora';
    case 'administrators/addUser':
      return 'add-admin';
    case 'Typy-usług/Dodaj-typ-usługi':
      return 'add-service-type';
    case 'Formularze-zadań/Podgląd-Formularza':
    case 'Formularze-zadań/Edytuj-Formularz':
      return 'szczegółów formularza zadania…';
    case 'Plany/Podgląd-planu':
      return 'szczegółów planu…';
    case 'Plany/Edycja-planu':
      return 'Edycja planu';
    case 'Plany/Utwórz-plan':
      return 'Utwórz plan';
    case 'Oferty/Utwórz-ofertę':
      return 'Utwórz ofertę';
    case 'Oferty/Edytuj-ofertę':
      return 'Edytuj ofertę';
    case 'technicians/Podgląd-technika':
      return 'Podgląd Technika';
    case 'technicians/Edycja-technika':
      return 'Edycja Technika';
    case 'Klienci/Podgląd-klienta':
      return 'Podgląd klienta';
    case 'Klienci/Edytuj-klienta':
      return 'Edycja klienta';
    case 'Klienci/Edycja-klienta':
      return segments.includes('krok-2') ? 'edit-client-step-2' : 'edit-client';
    case 'Typy-usług/Podgląd-typu-usługi':
      return 'szczegółów typu usługi…';
    case 'Produkty i usługi/Podgląd-zgłoszenia':
    case 'Zgłoszenia/Podgląd-zgłoszenia':
    case 'Zgłoszenia/Podgląd-mojego-zadania':
    case 'Moje-zadania/Podgląd-zgłoszenia':
      return 'szczegółów zgłoszenia…';
    case 'Moje-zadania/Podgląd-zgłoszenia/formularz-zadania':
      return 'formularza zadania…';
    case 'Zgłoszenia/Podgląd-zgłoszenia-kontaktowego':
    case 'Zgłoszenia/Podgląd-mojego-zadania-kontaktowego':
      return 'szczegółów zgłoszenia kontaktowego…';
    case 'add-admin':
    case 'add-service-type':
      return 'Ładowanie…';
    case 'Typy-usług/Edycja-typu-usługi':
      return 'Edycja typu usługi';
    case 'Certyfikaty/Podgląd-certyfikatu':
      return 'certificate-view';
    case 'Certyfikaty/Podgląd-szablonu':
      return 'certificate-template-view';
    case 'Certyfikaty/Edycja-szablonu':
      return 'certificate-template-edit';
    case 'Kategorie/Podgląd':
      return 'Podgląd kategorii';
    case 'Kategorie/Edycja':
      return 'Edytuj kategorię';
    case 'Przesłane-formularze/Podgląd':
      return 'submitted-form-view';
    default: {
      const last = segments[segments.length - 1];
      return segments.length > 0 ? (last ?? pathDecoded) : pathDecoded;
    }
  }
}

export default function Loader() {
  const location = useLocation();
  const label = getLoaderLabel(location.pathname);
  return (
    <div className="flex flex-col items-center justify-center gap-[24px]">
      <div
        style={{
          height: '100px',
          width: '100px',
        }}
      >
        <Lottie
          animationData={loaderAnimation}
          loop
          style={{
            height: '100%',
            width: '100%',
          }}
          rendererSettings={{
            preserveAspectRatio: 'xMidYMid meet',
          }}
        />
      </div>
      <p className="text-bodyLarge font-normal text-neutral-10 align-middle">
        {getLoaderText(label)}
      </p>
    </div>
  );
}
